import { useState } from 'react';
import { Megaphone, Plus } from 'lucide-react';
import type { Announcement } from '@electrodavisan/shared';
import { useCurrentUser } from '../../auth/useSession';
import {
  Button,
  Input,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHeader,
  SkeletonCards,
  useToast,
} from '../../components/ui';
import {
  useAllAnnouncements,
  useAnnouncements,
  useDeleteAnnouncement,
} from '../../services/announcements.service';
import { AnnouncementCard } from './AnnouncementCard';
import { AnnouncementFormModal } from './AnnouncementFormModal';

/**
 * El canal de anuncios.
 *
 * ## Una sola pagina, no dos
 *
 * Quien administra ve lo mismo que los demas, con los borradores intercalados
 * y los botones de editar y borrar. No hay una pantalla de administracion
 * aparte a proposito: asi quien publica ve el canal EXACTAMENTE como lo ve
 * todo el mundo, y no descubre que un anuncio se leia raro despues de que lo
 * haya leido media empresa.
 */
export function AnnouncementsPage(): JSX.Element {
  const { data: user } = useCurrentUser();
  const puedeEscribir = user?.isAdmin === true;

  // Quien administra pide la lista con borradores; el resto, solo lo publicado.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const canal = useAnnouncements(search, !puedeEscribir);
  const completo = useAllAnnouncements(puedeEscribir, search);
  const consulta = puedeEscribir ? completo : canal;

  const [formulario, setFormulario] = useState<{ abierto: boolean; anuncio: Announcement | null }>({
    abierto: false,
    anuncio: null,
  });
  const [porBorrar, setPorBorrar] = useState<Announcement | null>(null);

  const borrar = useDeleteAnnouncement();
  const toast = useToast();

  async function confirmarBorrado(): Promise<void> {
    if (!porBorrar) return;
    try {
      await borrar.mutateAsync(porBorrar.id);
      toast.success('Anuncio borrado');
    } catch (error) {
      toast.error('No se pudo borrar', error instanceof Error ? error.message : undefined);
    } finally {
      setPorBorrar(null);
    }
  }

  const anuncios = [...new Map((consulta.data?.pages.flatMap((page) => page.items) ?? [])
    .map((item) => [item.id, item])).values()];
  const total = consulta.data?.pages[0]?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Anuncios"
        description="Novedades, mantenimientos y avisos del ecosistema."
        actions={
          puedeEscribir ? (
            <Button
              leftIcon={<Plus className="h-4 w-4" aria-hidden />}
              onClick={() => setFormulario({ abierto: true, anuncio: null })}
            >
              Nuevo anuncio
            </Button>
          ) : undefined
        }
      />

      <form className="mb-4 flex flex-wrap items-end gap-3" onSubmit={(event) => {
        event.preventDefault(); setSearch(searchInput.trim());
      }}>
        <Input label="Buscar en todos los anuncios" value={searchInput} maxLength={160}
          onChange={(event) => setSearchInput(event.target.value)} />
        <Button type="submit">Buscar</Button>
        <Button variant="secondary" onClick={() => { setSearchInput(''); setSearch(''); }}>Ver todos</Button>
        <Button variant="secondary" loading={consulta.isRefetching} onClick={() => void consulta.refetch()}>Actualizar</Button>
      </form>
      <p className="mb-4 text-sm" role="status">
        {anuncios.length} mostrados de {total} anuncios{puedeEscribir ? ' (incluye borradores)' : ' publicados'}.
        {' '}Este total no representa los pendientes de leer.
      </p>
      {consulta.isError && !anuncios.length ? (
        <ErrorState onRetry={() => void consulta.refetch()} />
      ) : consulta.isLoading ? (
        <SkeletonCards count={3} />
      ) : anuncios.length === 0 ? (
        <EmptyState
          icon={<Megaphone className="h-6 w-6" aria-hidden />}
          title={search ? "No hay anuncios que coincidan" : "Todavia no hay anuncios"}
          description={
            puedeEscribir
              ? 'Cuando publiques uno, lo vera todo el mundo que entre al portal.'
              : 'Aqui apareceran las novedades y los avisos del ecosistema.'
          }
        />
      ) : (
        <div className="space-y-3">
          {anuncios.map((anuncio) => (
            <AnnouncementCard
              key={anuncio.id}
              announcement={anuncio}
              onEdit={
                puedeEscribir
                  ? (elegido) => setFormulario({ abierto: true, anuncio: elegido })
                  : undefined
              }
              onDelete={puedeEscribir ? setPorBorrar : undefined}
            />
          ))}
        </div>
      )}

      {consulta.hasNextPage && (
        <div className="my-4">
          {consulta.isFetchNextPageError && <p role="alert">No se pudo cargar la página. Puedes reintentar sin perder los anuncios visibles.</p>}
          <Button loading={consulta.isFetchingNextPage} onClick={() => void consulta.fetchNextPage()}>
            {consulta.isFetchNextPageError ? 'Reintentar' : 'Cargar más anuncios'}
          </Button>
        </div>
      )}

      <AnnouncementFormModal
        open={formulario.abierto}
        announcement={formulario.anuncio}
        onClose={() => setFormulario({ abierto: false, anuncio: null })}
      />

      <ConfirmDialog
        open={porBorrar !== null}
        title="Borrar el anuncio"
        message={
          porBorrar
            ? `"${porBorrar.title}" desaparecera del canal. No se puede deshacer.`
            : ''
        }
        confirmLabel="Borrar"
        tone="danger"
        onConfirm={() => void confirmarBorrado()}
        onCancel={() => setPorBorrar(null)}
      />
    </>
  );
}
